import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useLocation } from '@/services/router/compat';
import { createRealtimeBridge } from './bridge';
import { createCentrifugeConnection } from './centrifuge-connection';
import type { ActiveRealtime } from './events';
import { isDesignStudioPath } from './design-studio-route';
import type { Peer } from './peers';
import type { PeerStore } from './peer-store';
import { getTabId, onTabIdChange } from './tab-id';
import { fetchRealtimeToken } from './token-api';
import { useDsNodeChanges } from './use-ds-node-changes';
import { createWorkspacePresence, type WorkspacePresence } from './workspace-presence';

/**
 * Dispatched on `window` after the bridge is installed or removed. Embedded apps stay mounted while
 * the tab is elsewhere, so this is how they learn that a new connection brought a new bridge.
 */
export const REALTIME_BRIDGE_EVENT = 'hydra:realtime-bridge';

/** Installs `window.__hydra__.realtime` while `realtime` is set and removes it when it clears. */
function useRealtimeBridge(realtime: ActiveRealtime | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (realtime === null) return;
    const bridge = createRealtimeBridge({ ...realtime, queryClient });
    const hydra = (window.__hydra__ ??= {});
    hydra.realtime = bridge;
    window.dispatchEvent(new Event(REALTIME_BRIDGE_EVENT));
    return () => {
      // Read the global again: every authenticated fetch replaces window.__hydra__ with a copy.
      const installed = window.__hydra__;
      if (installed?.realtime !== bridge) return;
      installed.realtime = undefined;
      window.dispatchEvent(new Event(REALTIME_BRIDGE_EVENT));
    };
  }, [realtime, queryClient]);
}

const NO_PEERS: Peer[] = [];
const noopSubscribe = () => () => {};
const getNoPeers = () => NO_PEERS;

/** The workspace peer store while realtime is connected; null otherwise. */
const WorkspacePeersContext = createContext<PeerStore | null>(null);

/**
 * Every other tab in the workspace with its last `location`, or an empty list while realtime is
 * not connected (flag off, or not on a Design Studio page).
 */
export function useWorkspacePeers(): Peer[] {
  const store = useContext(WorkspacePeersContext);
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getList ?? getNoPeers);
}

/** Joins the workspace channel while connected and reports this tab's route on it. */
function useWorkspacePresence(realtime: ActiveRealtime | null, pathname: string): PeerStore | null {
  const [presence, setPresence] = useState<WorkspacePresence | null>(null);

  useEffect(() => {
    if (realtime === null) return;
    const joined = createWorkspacePresence(realtime.transport, realtime.environmentId);
    setPresence(joined);
    return () => {
      joined.close();
      setPresence(null);
    };
  }, [realtime]);

  useEffect(() => {
    presence?.setLocation(pathname);
  }, [presence, pathname]);

  return presence?.store ?? null;
}

type RealtimeProviderProps = { children: ReactNode };

/**
 * Opens one Centrifuge connection for the tab, for the current workspace, while the account has
 * the `ds-collaboration-in-services` flag and the route is a Design Studio page. Moving between
 * Design Studio pages keeps the connection; leaving Design Studio closes it. The children render
 * the same either way, so a flag flip or a route change never remounts the app below.
 */
export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const enabled = useFeatureFlag(FeatureFlag.DsCollaborationInServices);
  const { pathname } = useLocation();
  const workspace = useCurrentWorkspace();
  const environmentId = enabled && isDesignStudioPath(pathname) && workspace?.id != null ? workspace.id : null;
  const tab = useSyncExternalStore(onTabIdChange, getTabId);

  // Creating a connection is free; it only dials once started.
  const connection = useMemo(
    () => (environmentId === null ? null : createCentrifugeConnection(() => fetchRealtimeToken(environmentId), tab)),
    [environmentId, tab],
  );
  useEffect(() => {
    connection?.start();
    return () => connection?.stop();
  }, [connection]);

  const realtime = useMemo(
    () => (connection === null || environmentId === null ? null : { transport: connection, environmentId, tab }),
    [connection, environmentId, tab],
  );

  useRealtimeBridge(realtime);
  useDsNodeChanges(realtime);
  const workspacePeers = useWorkspacePresence(realtime, pathname);

  return <WorkspacePeersContext value={workspacePeers}>{children}</WorkspacePeersContext>;
}
